import { useState, useEffect, useRef } from 'react';

import useDebounceCallback from '@lib/core/src/hooks/useDebounceCallback';
import { Stack } from '@mui/material';

import { GoalDto } from '@/api/generated';
import FocusIcon from '@/assets/buttons-icons/focus.svg?react';
import Markdown from '@/utils/Markdown';

import useGoalsAddEdit from '../hooks/useGoalsAddEdit';
import { Container, FocusIconWrapper, HeaderRow, TextInput } from './GoalBoxStyles';

interface GoalBoxProps {
  goal: GoalDto | undefined;
  dueDate: string;
}

const DEBOUNCE_TIME = 700;

const GoalBox = ({ goal, dueDate }: GoalBoxProps) => {
  const [description, setDescription] = useState(goal?.title || '');
  const [isEditing, setIsEditing] = useState(false);
  const { onEditGoal, onAddGoal } = useGoalsAddEdit();
  const textInputRef = useRef<HTMLTextAreaElement>(null);

  const handleSave = async (value: string) => {
    if (goal?.id) {
      onEditGoal(value, goal.id || '');
    }
  };

  const handleAddGoal = async (value: string) => {
    const newGoalId = (await onAddGoal(value, dueDate)) || '';
  };

  const addOrEditGoal = async (value: string) => {
    if (goal?.id) {
      handleSave(value);
    } else {
      await handleAddGoal(value);
    }
  };

  useDebounceCallback(description, DEBOUNCE_TIME, addOrEditGoal);

  useEffect(() => {
    setDescription(goal?.title || '');
  }, [goal]);

  const handleChange = (newValue: string) => {
    setDescription(newValue);
  };

  const handleContainerClick = () => {
    setIsEditing(true);
  };

  useEffect(() => {
    if (isEditing) {
      textInputRef.current?.focus();
    }
  }, [isEditing]);

  return (
    <Container onClick={handleContainerClick}>
      <HeaderRow>
        <FocusIconWrapper>
          <FocusIcon />
        </FocusIconWrapper>
      </HeaderRow>
      <Stack sx={{ overflow: 'auto' }}>
        {goal?.title && !isEditing ? (
          <Markdown>{description}</Markdown>
        ) : (
          <TextInput
            ref={textInputRef}
            minRows={1}
            maxRows={5}
            placeholder="Start writing..."
            value={description}
            onChange={e => handleChange(e.target.value)}
          />
        )}
      </Stack>
    </Container>
  );
};

export default GoalBox;
